<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useToast } from 'primevue/usetoast';
import { useGuardianStore } from '@/store';
import { GuardianInterface } from '@/types';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface GuardianPropsInterface {
  tableData?: GuardianInterface[];
  studentId?: number;
  loading?: boolean;
}

interface GuardianTableInterface {
  id: number;
  display_name: string;
  relationship?: string;
}

const props = defineProps<GuardianPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const guardianStore = useGuardianStore();
const guardians: Ref<GuardianInterface[] | undefined> = toRef(props, 'tableData');
const guardianCount: Ref<number> = computed(() => (guardians.value ? guardians.value.length : 0));
const guardianModalOpen = ref(false);
const toast = useToast();
const guardianItems: Ref<{ name: string; value: number }[]> = ref([]);

const createMenuModel = (item: GuardianTableInterface) => {
  const menuItem = {
    label: 'Remove Guardian',
    command: () => {
      detachGuardian([item.id], item.display_name);
    },
    visible: can([PERMISSIONS.STUDENT_DETACH_CLINICIANS]),
  };
  return [menuItem].filter((item) => item.visible);
};

const attachGuardian: (ids: number[]) => Promise<void> = async (ids) => {
  await guardianStore.attachGuardians({ parents: ids, studentId: props.studentId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Guardian(s) has been added',
    life: 3000,
  });
};

const detachGuardian: (ids: number[], guardianName: string) => Promise<void> = async (ids, guardianName) => {
  await guardianStore.detachGuardians({ parents: ids, studentId: props.studentId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Guardian ${guardianName} has been removed from student`,
    life: 3000,
  });
};

const openAddGuardians = async () => {
  if (can([PERMISSIONS.STUDENT_CLINICIANS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.STUDENT_ATTACH_CLINICIANS])) {
    guardianItems.value = await guardianStore.getAddGuardiansModalData(props.studentId as number);
    guardianModalOpen.value = true;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add guardians',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (guardianModalOpen.value = false);

const onSaveAdd = (guardians: { name: string; value: number }[]) => {
  let guardianIds = guardians.map((guardian) => guardian.value);
  attachGuardian(guardianIds);
  guardianModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Guardians</h2>
      <Chip>{{ guardianCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.STUDENT_ATTACH_CLINICIANS])"
          type="text"
          size="small"
          text="Add Guardians"
          icon-left="add"
          @click="openAddGuardians"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="guardians"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-guardians-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="guardianCount">
          <Column body-class="mw-20rem" header-class="mw-20rem" field="display_name" header="Guardian Name" sortable>
            <template #body="{ data }">
              <a class="item-link font-semibold" :href="`/user/${data.id}`" v-text="data.display_name" />
            </template>
          </Column>
          <Column field="relationship" header="Relationship" />
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Guardians not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="guardianModalOpen && can([PERMISSIONS.STUDENT_CLINICIANS_AVAILABLE_TO_ADD])"
        title="Add Guardians"
        button-text="Add Guardians"
        :items="guardianItems"
        :searchable="true"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped></style>
